/**
 * ExportColumnConfigurationModal.js
 * Reusable modal component for configuring which columns to include/exclude in exports
 *
 * Can be used for:
 * - PDF exports (landscape layout with column constraints)
 * - Excel exports (width optimization)
 * - CSV exports (field selection)
 * - Any other export format that needs column selection
 *
 * Props:
 * - isOpen: Boolean indicating if modal is visible
 * - onClose: Function to call when modal should close
 * - title: Modal title (default: "Configure Columns")
 * - description: Optional description text
 * - columns: Object mapping column names to boolean (included/excluded)
 * - onColumnsChange: Function to call when column selection changes
 * - onApply: Optional function to call when "Apply" button is clicked (defaults to onClose)
 * - pdfMaxColumns: When set, shows PDF column limit advisory (enforced only when user runs PDF export)
 *
 * Usage:
 * <ExportColumnConfigurationModal
 *   isOpen={showConfig}
 *   onClose={() => setShowConfig(false)}
 *   title="Configure PDF Export Columns"
 *   description="Select which columns to include in the PDF export."
 *   columns={columnConfig}
 *   onColumnsChange={setColumnConfig}
 * />
 */

import React from 'react';
import Modal from './Modal';

const ExportColumnConfigurationModal = ({
  isOpen,
  onClose,
  title = 'Configure Columns',
  description,
  columns,
  onColumnsChange,
  onApply,
  pdfMaxColumns = null,
}) => {
  const handleColumnToggle = (columnName) => {
    onColumnsChange({
      ...columns,
      [columnName]: !columns[columnName],
    });
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(columns).every(val => val === true);
    const newColumns = {};
    Object.keys(columns).forEach(key => {
      newColumns[key] = !allSelected;
    });
    onColumnsChange(newColumns);
  };

  const allSelected = Object.values(columns).every(val => val === true);
  const selectedCount = Object.values(columns).filter(val => val === true).length;
  const overPdfLimit = pdfMaxColumns != null && selectedCount > pdfMaxColumns;

  const handleApply = () => {
    if (onApply) {
      onApply();
    } else {
      onClose();
    }
  };

  const footerButtons = (
    <button
      onClick={handleApply}
      style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: 'var(--dp-accent-primary)',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: '600',
        color: 'white',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = '#5568d3';
        e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = 'var(--dp-accent-primary)';
        e.target.style.boxShadow = 'none';
      }}
    >
      Apply
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="medium"
      footer={footerButtons}
    >
      {description && (
        <p style={{
          margin: '0 0 1.5rem 0',
          color: '#6b7280',
          fontSize: '0.9rem',
          fontWeight: '500',
          lineHeight: '1.5'
        }}>
          {description}
        </p>
      )}

      {pdfMaxColumns != null && (
        <p style={{
          margin: description ? '0 0 1rem 0' : '0 0 1rem 0',
          padding: '0.75rem 1rem',
          backgroundColor: overPdfLimit ? '#fef3c7' : 'var(--dp-accent-focus-ring)',
          border: `1px solid ${overPdfLimit ? '#f59e0b' : 'var(--dp-accent-focus-ring)'}`,
          borderRadius: '8px',
          color: '#1f2937',
          fontSize: '0.85rem',
          lineHeight: '1.5',
        }}>
          PDF export: <strong>{selectedCount}/{pdfMaxColumns}</strong> columns selected.
          {overPdfLimit
            ? ` Reduce to ${pdfMaxColumns} or fewer before exporting PDF, or use Excel for this selection.`
            : ' You can select any columns here; the limit applies only when you export to PDF.'}
        </p>
      )}

      {/* Select All Checkbox */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <input
          type="checkbox"
          checked={allSelected}
          onChange={handleSelectAll}
          style={{
            cursor: 'pointer',
            width: '18px',
            height: '18px',
            accentColor: 'var(--dp-accent-primary)',
            flexShrink: 0
          }}
        />
        <span style={{
          fontSize: '0.9rem',
          fontWeight: '600',
          color: '#1f2937',
          flex: 1
        }}>
          Select All Columns ({selectedCount}/{Object.keys(columns).length})
        </span>
      </div>

      {/* Column Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem'
      }}>
        {Object.keys(columns).map(columnName => (
          <label
            key={columnName}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              padding: '0.75rem',
              borderRadius: '6px',
              transition: 'all 0.2s',
              backgroundColor: columns[columnName] ? 'var(--dp-accent-focus-ring)' : 'transparent',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--dp-accent-focus-ring)';
              e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = columns[columnName] ? 'var(--dp-accent-focus-ring)' : 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <input
              type="checkbox"
              checked={columns[columnName]}
              onChange={() => handleColumnToggle(columnName)}
              style={{
                cursor: 'pointer',
                width: '18px',
                height: '18px',
                accentColor: 'var(--dp-accent-primary)'
              }}
            />
            <span style={{
              fontSize: '0.9rem',
              color: '#333',
              fontWeight: '500'
            }}>
              {columnName}
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
};

export default ExportColumnConfigurationModal;
